import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, fetchTerm, fetchCategory } from "../lib/api";
import { useTrustScore } from "../components/trust/TrustProvider";
import { TrustBadge, TrustBadgeLightbox } from "../components/trust/TrustBadge";
import { ChevronLeft, ExternalLink, Sparkles } from "lucide-react";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [terms, setTerms] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTrust, setOpenTrust] = useState(false);
  const { score } = useTrustScore(slug);

  useEffect(() => {
    setLoading(true);
    fetchProduct(slug)
      .then(async (p) => {
        setProduct(p);
        document.title = `${p.name} – Beckyliss`;
        const [termList, cat] = await Promise.all([
          Promise.all((p.term_slugs || []).map((t) => fetchTerm(t).catch(() => null))),
          fetchCategory(p.category_slug).catch(() => null),
        ]);
        setTerms(termList.filter(Boolean));
        setCategory(cat);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="max-w-editorial mx-auto px-6 py-32 text-center text-bl-textSoft">Chargement…</div>;
  }
  if (!product) {
    return (
      <div className="max-w-editorial mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <p className="font-serif text-3xl">Produit introuvable</p>
        <Link to="/" className="overline mt-6 inline-block">Retour</Link>
      </div>
    );
  }

  const formattedPrice = product.price
    ? `${product.price.toFixed(2).replace('.', ',')} €`
    : null;

  return (
    <div className="fade-in-up" data-testid={`product-page-${product.slug}`}>
      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 pt-12 pb-6">
        <div className="flex items-center gap-3 text-sm">
          <Link to="/" className="overline" data-testid="product-breadcrumb-home">Glossaire</Link>
          <span className="text-bl-textSoft">/</span>
          {category && (
            <Link to={`/categorie/${category.slug}`} className="overline" data-testid="product-breadcrumb-cat">
              {category.name}
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 pb-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <div className="aspect-square bg-bl-soft border border-bl-border overflow-hidden relative">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center">Beckyliss</div>
            )}
            {score && (
              <div
                style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
                onClick={() => setOpenTrust(true)}
              >
                <TrustBadge score={score.global_score} size={62} testId={`trust-badge-product-${product.slug}`} />
              </div>
            )}
          </div>
        </div>
        <div className="md:col-span-6 md:pl-6">
          <span className="overline">{product.sku || "Beckyliss"}</span>
          <h1 className="font-serif font-light text-4xl md:text-5xl text-bl-text mt-4 leading-tight text-balance">
            {product.name}
          </h1>
          {formattedPrice && (
            <p className="font-serif text-2xl text-bl-dore mt-6">{formattedPrice}</p>
          )}
          {product.description && (
            <p className="mt-6 text-base text-bl-textSoft leading-relaxed">
              {product.description}
            </p>
          )}
          <a
            href={product.product_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-bl-text text-bl-bg px-7 py-3.5 text-sm tracking-wide hover:bg-bl-dore transition-colors"
            data-testid="product-shop-link"
          >
            Voir sur Beckyliss <ExternalLink size={16} strokeWidth={1.4} />
          </a>

          <div className="mt-10 grid sm:grid-cols-2 gap-8">
            {product.benefits?.length > 0 && (
              <div>
                <span className="overline flex items-center gap-2"><Sparkles size={12} /> Bénéfices</span>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {product.benefits.map((b) => (
                    <li key={b} className="flex gap-2"><span className="text-bl-dore">—</span> {b}</li>
                  ))}
                </ul>
              </div>
            )}
            {product.usage && (
              <div>
                <span className="overline">Mode d'utilisation</span>
                <p className="mt-3 text-sm leading-relaxed">{product.usage}</p>
              </div>
            )}
            {product.key_ingredients?.length > 0 && (
              <div>
                <span className="overline">Ingrédients clés</span>
                <p className="mt-3 text-sm">{product.key_ingredients.join(" · ")}</p>
              </div>
            )}
            {product.concerns?.length > 0 && (
              <div>
                <span className="overline">Préoccupations</span>
                <p className="mt-3 text-sm">{product.concerns.join(" · ")}</p>
              </div>
            )}
            {product.ingredients_inci && (
              <div className="sm:col-span-2">
                <span className="overline">Composition INCI</span>
                <p className="mt-3 text-xs text-bl-textSoft leading-relaxed font-mono">
                  {product.ingredients_inci}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PERFUME OLFACTIVE PYRAMID */}
      {product.perfume_notes && (
        <section
          className="bg-gradient-to-b from-bl-rose/20 to-transparent border-t border-bl-border py-20 md:py-24"
          data-testid="product-perfume-pyramid"
        >
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
              <div>
                <span className="overline">Pyramide olfactive</span>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 leading-tight">
                  Les trois <em className="italic text-bl-dore">étages</em> du sillage.
                </h2>
              </div>
              {product.perfume_notes.olfactive_family && (
                <span
                  className="text-xs uppercase tracking-[0.18em] px-4 py-2 border border-bl-mauveDeep text-bl-mauveDeep rounded-full"
                  data-testid="perfume-olfactive-family"
                >
                  {product.perfume_notes.olfactive_family}
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-10">
              {[
                { key: "top",   title: "Notes de tête",  subtitle: "Premier contact — 5 à 15 min", accent: "#F5CE6F" },
                { key: "heart", title: "Notes de cœur",  subtitle: "L'âme — 2 à 4 heures",         accent: "#D6396C" },
                { key: "base",  title: "Notes de fond",  subtitle: "Le sillage — jusqu'à 8 h",     accent: "#7A4A6B" },
              ].map((step, idx) => {
                const notes = product.perfume_notes[step.key] || [];
                return (
                  <div
                    key={step.key}
                    data-testid={`perfume-pyramid-${step.key}`}
                    className="relative bg-white/60 backdrop-blur-sm border border-bl-border p-6 md:p-8"
                    style={{ borderTopWidth: 3, borderTopColor: step.accent }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span
                        className="w-8 h-8 rounded-full grid place-items-center text-xs font-serif italic"
                        style={{ background: step.accent, color: "white" }}
                      >
                        {idx + 1}
                      </span>
                      <div>
                        <h3 className="font-serif text-xl leading-tight">{step.title}</h3>
                        <p className="text-[11px] uppercase tracking-wider text-bl-textSoft mt-0.5">
                          {step.subtitle}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-2.5 mt-6">
                      {notes.map((n) => (
                        <li key={n} className="flex items-center gap-3 text-sm">
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ background: step.accent }}
                          />
                          <span className="text-bl-text">{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {terms.length > 0 && (
        <section className="bg-bl-soft border-t border-bl-border py-20" data-testid="product-related-terms">
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <span className="overline">Glossaire associé</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-10">Comprendre ce produit</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {terms.map((t) => (
                <Link
                  key={t.slug}
                  to={`/terme/${t.slug}`}
                  className="group block p-6 bg-bl-surface border border-bl-border"
                  data-testid={`product-related-term-${t.slug}`}
                >
                  <span className="overline">{t.category_slug}</span>
                  <h3 className="font-serif text-2xl text-bl-text group-hover:text-bl-dore transition-colors mt-3">
                    {t.name}
                  </h3>
                  <p className="text-sm text-bl-textSoft mt-3 leading-relaxed line-clamp-3">
                    {t.short_definition}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-16">
        {category && (
          <Link
            to={`/categorie/${category.slug}`}
            className="inline-flex items-center gap-2 text-sm overline"
            data-testid="product-back-cat"
          >
            <ChevronLeft size={14} /> Retour à {category.name}
          </Link>
        )}
      </section>

      <TrustBadgeLightbox
        open={openTrust}
        onClose={() => setOpenTrust(false)}
        slug={product.slug}
        initialData={score}
      />
    </div>
  );
}
